import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Input, Button, Row, Col, Spin, message, Checkbox } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Application } from '../../constants/Application';
import { SSOConfig } from '../../models/sso-config-interface';
import MetadataUrlModal from './MetadataUrlModal';
import CloseableErrorMessage from '../CloseableErrorMessage';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SSO_CONFIG_MODES, SSOConfigMode } from '../../constants/sso-config-modes';
import { processParsedXml } from '../../utilites/ssoHelper';

const { TextArea } = Input;

// Valid XML file types
const VALID_XML_TYPES = ['text/xml', 'application/xml', '.xml'];

type Props = {
  isModalVisible: boolean;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: number;
  mode: SSOConfigMode;
  isConfigured: boolean;
  onAddSuccess?: () => void; // Optional callback for successful add operations
};

const validationSchema = Yup.object().shape({
  entityId: Yup.string().required('Entity ID is required'),
  signOnUrl: Yup.string().url('Must be a valid URL').required('Sign-On URL is required'),
  certificate: Yup.string().required('Certificate is required'),
});

const processXmlFromFileUpload = async (file: File): Promise<SSOConfig | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const xmlContent = e.target?.result as string;
        const ssoConfig = processParsedXml(xmlContent);
        resolve(ssoConfig);
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(file);
  });
};

const SSOConfigModal: React.FC<Props> = ({ 
  isModalVisible,
  setIsModalVisible,
  companyId,
  mode,
  isConfigured,
  onAddSuccess
}) => {
  const [metadataUrlModalVisible, setMetadataUrlModalVisible] = useState(false);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [pendingMetadataConfig, setPendingMetadataConfig] = useState<SSOConfig | null>(null);
  const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);
  const submitFormRef = useRef<(() => void) | null>(null);

  // Determine applicationId from mode
  const applicationId = mode === SSO_CONFIG_MODES.ADD_ADMIN || mode === SSO_CONFIG_MODES.EDIT_ADMIN ? Application.Admin : Application.MemberApp;

  const [initialFormValues, setInitialFormValues] = useState<SSOConfig>({
    applicationId: applicationId,
    entityId: '',
    signOnUrl: '',
    certificate: '',
    enabled: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = mode === SSO_CONFIG_MODES.EDIT_ADMIN || mode === SSO_CONFIG_MODES.EDIT_MEMBER_APP;
  const isAddMode = mode === SSO_CONFIG_MODES.ADD_ADMIN || mode === SSO_CONFIG_MODES.ADD_MEMBER_APP;

  // React Query hooks
  const getSSOConfigQuery = CustomerServiceQueries.useGetSSOConfig(companyId, applicationId, isEditMode);
  const postSsoConfigMutation = CustomerServiceQueries.usePostSsoConfig();
  const putSsoConfigMutation = CustomerServiceQueries.usePutSsoConfig();

  // Helper variables for loading states
  const isLoadingData = getSSOConfigQuery.isRefetching || (isEditMode ? getSSOConfigQuery.isLoading : false);
  const isSubmitting = postSsoConfigMutation.isLoading || putSsoConfigMutation.isLoading;
  const isDisabled = isLoadingData || isSubmitting;

  // Update initial form values when SSO config is loaded
  useEffect(() => {
    if (getSSOConfigQuery.data) {
      setInitialFormValues({
        applicationId: applicationId,
        entityId: getSSOConfigQuery.data.entityId || '',
        signOnUrl: getSSOConfigQuery.data.signOnUrl || '',
        certificate: getSSOConfigQuery.data.certificate || '',
        enabled: getSSOConfigQuery.data.enabled,

      });
    } else if (isAddMode) {
      // Reset to empty values for add mode
      setInitialFormValues({
        applicationId: applicationId,
        entityId: '',
        signOnUrl: '',
        certificate: '',
        enabled: false,
      });
    }
  }, [getSSOConfigQuery.data, mode, applicationId, isAddMode]);

  // Apply pending metadata config from metadata url when available
  useEffect(() => {
    if (pendingMetadataConfig && setFieldValueRef.current) {
      setFieldValueRef.current('entityId', pendingMetadataConfig.entityId);
      setFieldValueRef.current('signOnUrl', pendingMetadataConfig.signOnUrl);
      setFieldValueRef.current('certificate', pendingMetadataConfig.certificate);
      setPendingMetadataConfig(null);
    }
  }, [pendingMetadataConfig]);

  const handleSubmit = async (values: SSOConfig) => {
    // Use putSsoConfig for edit modes, postSsoConfig for add modes
    if (isEditMode) {
      await putSsoConfigMutation.mutateAsync({ companyId, data: values });
      message.success('SSO configuration updated successfully.');
    } else {
      await postSsoConfigMutation.mutateAsync({ companyId, data: values });
      message.success('SSO configuration added successfully.');
      if (onAddSuccess) {
        onAddSuccess();
      }
    }
    
    setIsModalVisible(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: (field: string, value: any) => void) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isValidType = VALID_XML_TYPES.some(type => 
      file.type === type || file.name.toLowerCase().endsWith('.xml')
    );
    
    if (!isValidType) {
      CloseableErrorMessage('Please select a valid XML file.');
      return;
    }

    setIsFileProcessing(true);
    try {
      const ssoConfig = await processXmlFromFileUpload(file);
      
      if (ssoConfig) {
        // Clean certificate by removing all whitespace and newlines
        const cleanedCertificate = ssoConfig.certificate.replace(/\s+/g, '');
        
        setFieldValue('entityId', ssoConfig.entityId);
        setFieldValue('signOnUrl', ssoConfig.signOnUrl);
        setFieldValue('certificate', cleanedCertificate);
      } else {
        CloseableErrorMessage('Unable to parse the XML file. Please check the file format and try again.');
      }
    } finally {
      setIsFileProcessing(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper function to determine modal title based on mode
  const getModalTitle = (): string => {
    switch (mode) {
      case SSO_CONFIG_MODES.EDIT_ADMIN:
        return 'Edit Admin SSO Configuration';
      case SSO_CONFIG_MODES.EDIT_MEMBER_APP:
        return 'Edit Member App SSO Configuration';
      case SSO_CONFIG_MODES.ADD_ADMIN:
        return 'Add Admin SSO Configuration';
      case SSO_CONFIG_MODES.ADD_MEMBER_APP:
        return 'Add Member App SSO Configuration';
      default:
        return 'SSO Configuration';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      visible={isModalVisible}
      onCancel={() => !isDisabled && setIsModalVisible(false)}
      footer={[
        <Button 
          key="cancel"
          onClick={() => setIsModalVisible(false)} 
          disabled={isDisabled}
        >
          Cancel
        </Button>,
        <Button 
          key="submit"
          type="primary" 
          loading={isSubmitting}
          disabled={isLoadingData}
          onClick={() => {
            // Trigger form submission using stored submit function
            if (submitFormRef.current) {
              submitFormRef.current();
            }
          }}
        >
          {isEditMode ? 'Update' : 'Save'}
        </Button>
      ]}
      width={800}
      closeIcon={
        <Button
          icon={<FontAwesomeIcon icon={faTimes as IconProp} />}
          className="close-icon"
          disabled={isDisabled}
        >
        </Button>
      }
    >
      <Spin spinning={isDisabled}>
        <Formik
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikHandleSubmit, setFieldValue }) => {
            // Store setFieldValue in ref
            setFieldValueRef.current = setFieldValue;
            // Store the submit function in ref
            submitFormRef.current = formikHandleSubmit;

            return (
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col span={24}>
                    <div style={{ marginBottom: 16 }}>
                      <Button 
                        type="default" 
                        onClick={() => setMetadataUrlModalVisible(true)}
                        disabled={isDisabled}
                        style={{ marginRight: 8 }}
                      >
                        Load from Metadata URL
                      </Button>
                      <Button 
                        type="default" 
                        onClick={() => fileInputRef.current?.click()}
                        loading={isFileProcessing}
                        disabled={isDisabled}
                      >
                        Upload XML File
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xml,text/xml,application/xml"
                        style={{ display: 'none' }}
                        onChange={(event) => handleFileUpload(event, setFieldValue)}
                      />
                    </div>
                  </Col>
                </Row>

                <Form.Item
                  label="Entity ID"
                  validateStatus={errors.entityId && touched.entityId ? 'error' : ''}
                  help={errors.entityId && touched.entityId ? errors.entityId : ''}
                >
                  <Input
                    name="entityId"
                    value={values.entityId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter the Entity ID"
                    disabled={isDisabled || isSubmitting}
                  />
                </Form.Item>

                <Form.Item
                  label="Sign-On URL"
                  validateStatus={errors.signOnUrl && touched.signOnUrl ? 'error' : ''}
                  help={errors.signOnUrl && touched.signOnUrl ? errors.signOnUrl : ''}
                >
                  <Input
                    name="signOnUrl"
                    value={values.signOnUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter the Sign-On URL"
                    disabled={isDisabled || isSubmitting}
                  />
                </Form.Item>

                <Form.Item
                  label="Certificate"
                  validateStatus={errors.certificate && touched.certificate ? 'error' : ''}
                  help={errors.certificate && touched.certificate ? errors.certificate : ''}
                >
                  <TextArea
                    name="certificate"
                    value={values.certificate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Paste the certificate here"
                    rows={6}
                    disabled={isDisabled || isSubmitting}
                  />
                </Form.Item>

                {isConfigured && <Form.Item>
                  <Checkbox
                    name="enabled"
                    checked={values.enabled}
                    onChange={handleChange}
                  >
                    Enable SSO
                  </Checkbox>
                </Form.Item>}
              </Form>
            );
          }}
        </Formik>

        <MetadataUrlModal
          isModalVisible={metadataUrlModalVisible}
          setIsModalVisible={setMetadataUrlModalVisible}
          applicationId={applicationId}
          onSubmit={(ssoConfig) => {
            setPendingMetadataConfig(ssoConfig);
            setMetadataUrlModalVisible(false);
          }}
        />
      </Spin>
    </Modal>
  );
};

export default SSOConfigModal;
